import { evaluatePricingExpression, solvePricingMultiplier } from './pricingExpression';
const get = (row, key) => row?.[key] ?? row?.[key[0].toLowerCase() + key.slice(1)];

export const withPreviewMultiplier = (formulas, stationId, multiplier) => multiplier == null ? formulas : formulas.map(row =>
  Number(get(row, 'MasterStationId')) === Number(stationId) ? { ...row, SmallNetMultiplier: multiplier } : row);

const pricingVariables = (formula, { autoPickTotal, setupAmount, setupMultiplier }) => ({
  'List Price': autoPickTotal, 'Sum of Options': autoPickTotal,
  SmallLaborMultiplier: get(formula, 'SmallLaborMultiplier'),
  SmallLaborAdder: get(formula, 'SmallLaborAdder'),
  SmallOverheadMultiplier: get(formula, 'SmallOverheadMultiplier'),
  SmallNetMultiplier: get(formula, 'SmallNetMultiplier'),
  'Setup Multiplier Amount': setupAmount, 'Setup Multiplier Percent': setupMultiplier,
});

export function comparePreviewPrice(inputs, target) {
  const current = calculateFinalSalesPrice(inputs);
  if (current.error) throw new Error(current.error);
  const formula = inputs.formulas.find(row => Number(get(row, 'MasterStationId')) === Number(inputs.stationId));
  return solvePricingMultiplier(get(formula, 'Formula'), pricingVariables(formula, inputs), target);
}

export function calculateMarginPercentages({ formulas = [], stationId }) {
  // Margin percentages depend on the multiplier, not dummy price inputs.
  if (formulas.filter(row => Number(get(row, 'MasterStationId')) === Number(stationId)).length !== 1) return { error: 'Exactly one pricing formula is required for this station.' };
  const formula = formulas.find(row => Number(get(row, 'MasterStationId')) === Number(stationId));
  const multiplier = Number(get(formula, 'SmallNetMultiplier'));
  if (!Number.isFinite(multiplier) || !(multiplier > 0)) return { error: 'Net multiplier must be greater than zero and finite.' };
  const grossPercent = (1 - 1 / multiplier) * 100;
  return { multiplier, grossPercent, netPercent: grossPercent - 24.5, error: '' };
}

// Evaluate supported arithmetic without executing JavaScript from the API.
export function calculateFinalSalesPrice({ formulas = [], stationId, stationName = '', autoPickTotal, setupAmount, setupMultiplier }) {
  try {
    if (['vertical turbine', 'horizontal centrifugal', 'vertical multistage'].includes(stationName.trim().toLowerCase())) {
      throw new Error('Final Sales Price requires a pricing formula for this station type.');
    }
    const matches = formulas.filter(row => Number(get(row, 'MasterStationId')) === Number(stationId));
    if (matches.length !== 1) throw new Error(matches.length ? 'Multiple pricing formulas match this station.' : 'No pricing formula is configured for this station.');
    const formula = matches[0];
    if (String(get(formula, 'FormulaType')).trim().toLowerCase() !== 'small station') throw new Error('This pricing formula type is not supported yet.');
    const value = evaluatePricingExpression(get(formula, 'Formula'), pricingVariables(formula, { autoPickTotal, setupAmount, setupMultiplier }));
    if (!Number.isFinite(value)) throw new Error('Final Sales Price could not be calculated.');
    return { value: Math.round((value + Number.EPSILON) * 100) / 100, error: '' };
  } catch (error) { return { value: null, error: error.message }; }
}

export function calculateMarginAmounts(finalSalesPrice, margin) {
  if (finalSalesPrice?.error || margin?.error || !Number.isFinite(finalSalesPrice?.value)
    || !Number.isFinite(margin?.grossPercent) || !Number.isFinite(margin?.netPercent)) {
    return { grossAmount: null, netAmount: null };
  }
  const amount = percent => {
    const value = finalSalesPrice.value * percent / 100;
    return Number.isFinite(value) ? Number(value.toFixed(2)) : null;
  };
  return { grossAmount: amount(margin.grossPercent), netAmount: amount(margin.netPercent) };
}

export function getPreviewPricingInputs(model) {
  const fields = (model.sections || []).flatMap(section => section.subSections.flatMap(sub => [...sub.fields, ...(sub.disabledFields || [])]));
  const find = names => fields.find(field => names.includes(String(field.fieldName).trim().toLowerCase()));
  const value = field => field ? (model.effectiveInputs?.[field.id] ?? field.inputValue ?? field.selectedValue?.optionValue) : undefined;
  const setupMultiplier = value(find(['setup multiplier', 'setup multiplier percent'])) ?? 1.1;
  const costs = ['freight', 'overseas shipment', 'setup allowance', 'startup allowance', 'start up allowance', 'crane rental', 'inspection fees', 'other installation misc.', 'other installation misc'];
  const charges = fields.filter(field => costs.includes(String(field.fieldName).trim().toLowerCase()));
  const subtotal = charges.reduce((total, field) => total + Number(value(field) ?? 0), 0);
  const validMultiplier = String(setupMultiplier).trim() !== '' && Number.isFinite(Number(setupMultiplier)) && Number(setupMultiplier) >= 0;
  const calculated = subtotal * Number(setupMultiplier);
  const setupAmount = validMultiplier && Number.isFinite(calculated) ? Number(calculated.toFixed(2)) : undefined;
  return { setupAmount, setupMultiplier };
}

export function getSetupAmountPreviewFields(subSection) {
  const fields = [...(subSection.fields || []), ...(subSection.disabledFields || [])];
  const normalizedName = field => String(field.fieldName).trim().toLowerCase();
  const multiplier = fields.find(field => ['setup multiplier', 'setup multiplier percent'].includes(normalizedName(field)));
  if (!multiplier) return fields;
  const { setupAmount } = getPreviewPricingInputs({ sections: [{ subSections: [subSection] }] });
  const amount = fields.find(field => normalizedName(field) === 'setup multiplier amount');
  const calculated = { ...(amount || { id: `setup-amount-${multiplier.id}`, fieldName: 'Setup Multiplier Amount', sortOrder: Number(multiplier.sortOrder || 0) + 0.1 }),
    fieldType: 'Calculated', isDisabled: true, inputValue: setupAmount ?? '—' };
  return amount ? fields.map(field => field === amount ? calculated : field) : [...fields, calculated];
}
